import React from "react";
import { Link, useLocation } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";
import { isActivePath, TAB_ITEMS } from "./navItems";

interface Props {
  onMore: () => void;
}

/**
 * One-thumb access to the queues, on a phone.
 *
 * Without this every page change is two taps and a scroll through thirty
 * drawer entries, which is most of a working day for staff who triage from
 * their phones. The fifth slot opens the drawer, so nothing becomes
 * unreachable — the bar is a shortcut over the drawer, not a replacement.
 *
 * `z-10` matches the header rather than exceeding it: the drawer's `z-20`
 * backdrop then covers this too, so opening the menu dims the whole screen
 * instead of leaving a live strip along the bottom.
 */
export default function BottomTabBar({ onMore }: Props) {
  const { pathname } = useLocation();

  const tabClass = (active: boolean) =>
    `flex flex-col items-center justify-center gap-0.5 min-h-[56px] text-[11px] font-medium transition-colors ${
      active ? "text-indigo-600" : "text-gray-500"
    }`;

  return (
    <nav
      aria-label="Primary"
      className="fixed bottom-0 inset-x-0 z-10 md:hidden grid grid-cols-5 bg-white border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
    >
      {TAB_ITEMS.map(({ label, path, icon: Icon }) => {
        const active = isActivePath(path, pathname);
        return (
          <Link
            key={path}
            to={path}
            aria-current={active ? "page" : undefined}
            className={tabClass(active)}
          >
            <Icon size={18} />
            {label}
          </Link>
        );
      })}

      <button
        type="button"
        onClick={onMore}
        aria-label="More destinations"
        className={tabClass(false)}
      >
        <MoreHorizontal size={18} />
        More
      </button>
    </nav>
  );
}
